import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import {
  deleteCohortFailure,
  deleteCohortRequest,
  deleteCohortsuccess,
  fetchCohortsFailure,
  fetchCohortsRequest,
  fetchCohortsSuccess,
  fetchCohortStatsFailure,
  fetchCohortStatsSuccess,
  fetchSingleCohortFailure,
  fetchSingleCohortRequest,
  fetchSingleCohortSuccess,
  upsertCohortFailure,
  upsertCohortRequest,
  upsertCohortSuccess,
} from 'actions/cohorts';

import { API, Cohort, ErrorObject, UUID } from 'types';

export type CohortStats = {
  batches: {
    batch_uuid: UUID;
    license_transfers: {
      insight: number;
      prevent: number;
    };
  }[];
  license_transfers: {
    insight: number;
    prevent: number;
  };
};

type CohortsState = {
  loading: string[];
  error: ErrorObject | null;
  cohorts: Cohort[];
  activeCohort: Cohort | null;
  count: number;
  cohortStats: CohortStats | null;
};

const defaultState: CohortsState = {
  loading: [],
  error: null,
  cohorts: [],
  activeCohort: null,
  count: 0,
  cohortStats: null,
};
type CohortReducer<P = void> = (state: CohortsState, payload: PayloadAction<P>) => CohortsState;

const handleFetchSingleCohortRequest: CohortReducer = (state) => ({
  ...state,
  loading: [...state.loading, fetchSingleCohortRequest.toString()],
});

const handleFetchSingleCohortSuccess: CohortReducer<Cohort> = (state, { payload }) => {
  const cohort = state.cohorts.find((p) => p.uuid === payload.uuid);

  if (cohort) {
    return {
      ...state,
      cohorts: state.cohorts.map((p) => (p.uuid === payload.uuid ? { ...p, ...payload } : p)),
      activeCohort: payload,
      loading: state.loading.filter((s) => s !== fetchSingleCohortRequest.toString()),
    };
  }

  return {
    ...state,
    cohorts: [payload, ...state.cohorts],
    activeCohort: payload,
    loading: state.loading.filter((s) => s !== fetchSingleCohortRequest.toString()),
  };
};

const handleFetchSingleCohortFailure: CohortReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchSingleCohortRequest.toString()),
});

const handleUpsertCohortRequest: CohortReducer = (state) => ({
  ...state,
  loading: [...state.loading, upsertCohortRequest.toString()],
});

const handleUpsertCohortsuccess: CohortReducer<Cohort> = (state, { payload }) => {
  const cohort = state.cohorts.find((p) => p.uuid === payload.uuid);

  if (cohort) {
    return {
      ...state,
      cohorts: state.cohorts.map((p) => (p.uuid === payload.uuid ? { ...p, ...payload } : p)),
      activeCohort: { ...cohort, ...payload },
      loading: state.loading.filter((s) => s !== upsertCohortRequest.toString()),
    };
  }

  return {
    ...state,
    cohorts: [payload, ...state.cohorts],
    activeCohort: payload,
    loading: state.loading.filter((s) => s !== upsertCohortRequest.toString()),
    count: state.count + 1,
  };
};

const handleUpsertCohortFailure: CohortReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== upsertCohortRequest.toString()),
});

const handleDeleteCohortRequest: CohortReducer = (state) => ({
  ...state,
  loading: [...state.loading, deleteCohortRequest.toString()],
});

const handleDeleteCohortsuccess: CohortReducer<{ uuid: UUID }> = (state, { payload }) => {
  const newContracts = state.cohorts.filter((p) => p.uuid !== payload.uuid);
  return {
    ...state,
    cohorts: newContracts,
    loading: state.loading.filter((s) => s !== deleteCohortRequest.toString()),
    count: state.count - 1,
  };
};

const handleDeleteCohortFailure: CohortReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== deleteCohortRequest.toString()),
});

const handleFetchCohortsRequest: CohortReducer = (state) => ({
  ...state,
  loading: [...state.loading, fetchCohortsRequest.toString()],
});

const handleFetchCohortsSuccess: CohortReducer<API.WrappedAPIResponse<Cohort>> = (
  state,
  { payload }
) => ({
  ...state,
  cohorts: payload.records,
  count: payload.count,
  loading: state.loading.filter((s) => s !== fetchCohortsRequest.toString()),
});

const handleFetchCohortsFailure: CohortReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchCohortsRequest.toString()),
});

const handleFetchCohortStatsSuccess: CohortReducer<CohortStats> = (state, { payload }) => ({
  ...state,
  cohortStats: payload,
});

const handleFetchCohortStatsFailure: CohortReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  cohortStats: null,
  error: payload,
});

const handlers = {
  [fetchCohortsRequest.toString()]: handleFetchCohortsRequest,
  [fetchCohortsFailure.toString()]: handleFetchCohortsFailure,
  [fetchCohortsSuccess.toString()]: handleFetchCohortsSuccess,

  [fetchSingleCohortSuccess.toString()]: handleFetchSingleCohortSuccess,
  [fetchSingleCohortFailure.toString()]: handleFetchSingleCohortFailure,
  [fetchSingleCohortRequest.toString()]: handleFetchSingleCohortRequest,

  [upsertCohortRequest.toString()]: handleUpsertCohortRequest,
  [upsertCohortFailure.toString()]: handleUpsertCohortFailure,
  [upsertCohortSuccess.toString()]: handleUpsertCohortsuccess,

  [deleteCohortRequest.toString()]: handleDeleteCohortRequest,
  [deleteCohortsuccess.toString()]: handleDeleteCohortsuccess,
  [deleteCohortFailure.toString()]: handleDeleteCohortFailure,

  [fetchCohortStatsSuccess.toString()]: handleFetchCohortStatsSuccess,
  [fetchCohortStatsFailure.toString()]: handleFetchCohortStatsFailure,
};

const entitiesReducer = createReducer(defaultState, handlers);

export default entitiesReducer;
